/**
 * Assign a draft itinerary: field tech + start date (the trip's first
 * weekday). This is the commit step - every stop is driven through the
 * workflow ``schedule`` action server-side; warnings (e.g. the inspector
 * already has visits in the trip window) come back non-blocking and are
 * surfaced before the dialog closes.
 *
 * Form state is owned by the canonical `useForm` helper from
 * @/shared/lib/forms (see CLAUDE.md - Hand-Rolled Forms Are Banned). The
 * inspector picker is a composite widget so it rides the `custom` field
 * type; the start date is the standard generated `datepicker`.
 */

import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import { toast } from 'sonner'
import { Button } from '@/shared/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import { toastApiError } from '@/shared/lib/api-error'
import { useForm } from '@/shared/lib/forms'
import type { FormField } from '@/shared/lib/forms'
import { SearchablePicker } from '@/admin/pages/inspections/searchable-picker'
import {
  useAssignItinerary,
  useInspectorSearch,
  useReassignItinerary,
  type ItinerarySummary,
} from '@/admin/hooks/use-inspections'

type AssignFormValues = {
  inspector_uuid: string
  start_date: string
  notify?: boolean
}

/**
 * The field-tech search box, owning its own query state.
 *
 * This lives in its own component for a specific reason. It renders inside a
 * `type:'custom'` field, and `useForm` memoises the whole field config: any
 * state the render closure reads has to be declared in `deps`, but declaring
 * per-keystroke state there busts the memo on every character and REMOUNTS
 * every input in the form. Either way the box breaks - stale closure and it
 * ignores you, fresh closure and it keeps only the first letter.
 *
 * Keeping the search text and its query in here escapes the dilemma: the
 * closure above captures nothing that changes, so `deps` stay stable and
 * nothing remounts, while typing re-renders only this component.
 */
export function FieldTechPicker({
  selectedUuid,
  initialLabel,
  error,
  onChange,
}: {
  selectedUuid: string
  initialLabel: string
  error?: string
  onChange: (uuid: string) => void
}) {
  const { t } = useTranslation()
  const [search, setSearch] = useState('')
  const [label, setLabel] = useState(initialLabel)
  const { data: results, isLoading } = useInspectorSearch(search)

  return (
    <div className="flex flex-col gap-1.5">
      <SearchablePicker
        label={t('inspections.assign.fieldTechLabel', 'Field tech (FTS)')}
        required
        search={search}
        onSearchChange={setSearch}
        results={results}
        isLoading={isLoading}
        selectedUuid={selectedUuid}
        selectedLabel={label}
        onSelect={(uuid, primary) => {
          setLabel(primary)
          onChange(uuid)
        }}
        onClear={() => {
          setLabel('')
          onChange('')
        }}
        getOptionUuid={(u) => u.uuid}
        getOptionPrimary={(u) => u.full_name || u.email}
        getOptionSecondary={(u) =>
          u.full_name && u.email !== u.full_name ? u.email : undefined
        }
        placeholder={t(
          'inspections.assign.searchPlaceholder',
          'Search field tech by name or email…',
        )}
      />
      {error && (
        <p className="text-[11px] text-[var(--status-error)]">{error}</p>
      )}
    </div>
  )
}

export function AssignItineraryDialog({
  open,
  onOpenChange,
  itinerary,
  onAssigned,
  mode = 'assign',
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  itinerary: ItinerarySummary
  onAssigned?: () => void
  /**
   * `assign` commits a draft (both fields required - the trip has neither
   * yet). `reassign` moves an already-scheduled trip, so both fields are
   * pre-filled with what it has and either may be left alone: changing only
   * the date keeps the tech, changing only the tech keeps the dates.
   */
  mode?: 'assign' | 'reassign'
}) {
  const { t } = useTranslation()
  const isReassign = mode === 'reassign'
  const assign = useAssignItinerary(itinerary.uuid)
  const reassign = useReassignItinerary(itinerary.uuid)
  const pending = isReassign ? reassign.isPending : assign.isPending
  const [warnings, setWarnings] = useState<string[]>([])

  const fields = useMemo<FormField[]>(
    () => [
      {
        type: 'custom',
        name: 'inspector_uuid',
        value: isReassign ? (itinerary.inspector_uuid ?? '') : '',
        validationSchema: isReassign
          ? z.string().trim()
          : z
              .string()
              .trim()
              .min(
                1,
                t('inspections.assign.pickFieldTechError', 'Pick a field tech.'),
              ),
        // The closure captures NOTHING that changes while the dialog is
        // open - the picker owns its own search state - so `deps` stay
        // stable and the input is never remounted mid-typing.
        render: (formContext) => (
          <FieldTechPicker
            selectedUuid={(formContext.values.inspector_uuid as string) ?? ''}
            initialLabel={isReassign ? itinerary.inspector_name || '' : ''}
            error={formContext.errors.inspector_uuid as string | undefined}
            onChange={(uuid) => formContext.setFieldValue('inspector_uuid', uuid)}
          />
        ),
      },
      // Only when MOVING: assigning a draft is the first time anyone hears
      // about the trip, so there is nothing to mute. Ticked by default -
      // today's behaviour is "always notify", and an unticked default would
      // silently stop letters OC currently receive.
      ...(isReassign
        ? [
            {
              type: 'checkbox' as const,
              name: 'notify',
              label: t(
                'inspections.assign.notifyLabel',
                'Tell the homeowner, contractor and inspector',
              ),
              hint: t(
                'inspections.assign.notifyHint',
                'Untick to move this trip quietly - use it for corrections nobody needs a letter about.',
              ),
              value: true,
            },
          ]
        : []),
      {
        type: 'datepicker',
        name: 'start_date',
        label: t('inspections.assign.tripStartDateLabel', 'Trip start date'),
        testId: 'itinerary-start-date',
        value: isReassign ? (itinerary.start_date ?? '') : '',
        disablePast: true,
        validationSchema: isReassign
          ? z.string().trim()
          : z
              .string()
              .trim()
              .min(
                1,
                t('inspections.assign.pickStartDateError', 'Pick a start date.'),
              ),
      },
    ],
    [
      isReassign,
      itinerary.inspector_uuid,
      itinerary.inspector_name,
      itinerary.start_date,
      t,
    ],
  )

  const { Form, FormFooter } = useForm(
    {
      name: 'assign-itinerary-form',
      submit: 'footer',
      fields,
    },
    async (rawValues) => {
      const values = rawValues as unknown as AssignFormValues
      try {
        if (isReassign) {
          // Send only what actually moved: the backend keeps whatever it is
          // not given, so an unchanged field must not be echoed back as a
          // "change" and re-drive every stop for nothing.
          const changedInspector =
            values.inspector_uuid && values.inspector_uuid !== itinerary.inspector_uuid
              ? values.inspector_uuid
              : undefined
          const changedStart =
            values.start_date && values.start_date !== itinerary.start_date
              ? values.start_date
              : undefined
          if (!changedInspector && !changedStart) {
            toast.info(
              t(
                'inspections.assign.nothingChanged',
                'Nothing changed - pick a different tech or date.',
              ),
            )
            return
          }
          const res = await reassign.mutateAsync({
            inspector_uuid: changedInspector,
            start_date: changedStart,
            notify: values.notify !== false,
          })
          const bits = [
            t('inspections.assign.movedStops', 'Moved {{count}} stops.', {
              count: res.rescheduled.length,
            }),
          ]
          if (res.skipped.length)
            bits.push(
              t('inspections.assign.skippedCount', '{{count}} skipped.', {
                count: res.skipped.length,
              }),
            )
          toast.success(bits.join(' '))
          if (res.warnings.length) {
            setWarnings(res.warnings)
            onAssigned?.()
            return // keep open so the warnings are read
          }
          onAssigned?.()
          onOpenChange(false)
          return
        }
        const res = await assign.mutateAsync({
          inspector_uuid: values.inspector_uuid,
          start_date: values.start_date,
        })
        const bits = [
          t(
            'inspections.assign.scheduledSummary',
            res.days.length === 1
              ? 'Scheduled {{stops}} stops across {{count}} day.'
              : 'Scheduled {{stops}} stops across {{count}} days.',
            { stops: res.scheduled.length, count: res.days.length },
          ),
        ]
        if (res.skipped.length)
          bits.push(
            t('inspections.assign.skippedCount', '{{count}} skipped.', {
              count: res.skipped.length,
            }),
          )
        toast.success(bits.join(' '))
        if (res.warnings.length) {
          setWarnings(res.warnings)
          onAssigned?.()
          return // keep open so the warnings are read
        }
        onAssigned?.()
        onOpenChange(false)
      } catch (e) {
        toastApiError(
          e,
          isReassign
            ? t(
                'inspections.assign.moveError',
                'Could not move this itinerary.',
              )
            : t(
                'inspections.assign.assignError',
                'Could not assign this itinerary.',
              ),
        )
      }
    },
    // Deliberately stable, and `fields` is deliberately absent - it never did
    // anything here, because deps are compared with JSON.stringify and field
    // configs serialise identically whichever state changed. That is what
    // made this picker impossible to type in: the memo kept handing back the
    // closure built on first render. Listing per-keystroke state here would bust the
    // memo on every character and REMOUNT every input - which is how the
    // first attempt at this fix turned a dead box into one that only kept
    // the first letter. The picker re-renders itself instead.
    //
    // `itinerary.inspector_uuid` / `itinerary.start_date` ARE listed: the
    // submit closure compares against them to decide what actually moved, and
    // they are stable while the dialog is open (they change only if the
    // itinerary is refetched), so listing them refreshes the closure without
    // remounting anything.
    [
      open,
      itinerary.uuid,
      isReassign,
      itinerary.inspector_name,
      itinerary.inspector_uuid,
      itinerary.start_date,
    ],
  )

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {isReassign
              ? t('inspections.assign.dialogTitleMove', 'Move {{name}}', {
                  name: itinerary.name,
                })
              : t('inspections.assign.dialogTitle', 'Assign {{name}}', {
                  name: itinerary.name,
                })}
          </DialogTitle>
          <DialogDescription>
            {t(
              'inspections.assign.dialogDescription',
              itinerary.day_count === 1
                ? '{{stops}} stops over {{count}} weekday from the start date. Weekends are skipped automatically.'
                : '{{stops}} stops over {{count}} weekdays from the start date. Weekends are skipped automatically.',
              { stops: itinerary.stop_count, count: itinerary.day_count },
            )}
            {isReassign
              ? t(
                  'inspections.assign.dialogDescriptionMoveSuffix',
                  ' Change the tech, the dates, or both - anything you leave alone stays as it is.',
                )
              : ''}
          </DialogDescription>
        </DialogHeader>

        {warnings.length ? (
          <div className="flex flex-col gap-2 rounded-lg border border-[var(--rule)] bg-[var(--bg-light)] p-3">
            <p className="text-xs font-medium text-[var(--status-warn,#92400e)]">
              {t('inspections.assign.scheduledWithWarnings', 'Scheduled with warnings')}
            </p>
            {warnings.map((w) => (
              <p key={w} className="text-xs text-[var(--ink-2)]">
                {w}
              </p>
            ))}
          </div>
        ) : (
          <Form className="flex flex-col gap-4 py-1" />
        )}

        <DialogFooter>
          {warnings.length ? (
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              {t('inspections.assign.closeButton', 'Close')}
            </Button>
          ) : (
            FormFooter && (
              <FormFooter
                onCancel={() => onOpenChange(false)}
                submitText={
                  isReassign
                    ? pending
                      ? t('inspections.assign.movingButton', 'Moving…')
                      : t('inspections.assign.moveButton', 'Move trip')
                    : pending
                      ? t('inspections.assign.schedulingButton', 'Scheduling…')
                      : t(
                          'inspections.assign.assignButton',
                          'Assign + schedule',
                        )
                }
              />
            )
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
